import { Injectable } from '@angular/core';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { INameIdObj, INameIdTypeObjWithChildren } from '@fi-shared/fi-shared-interfaces';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import {
    IBusinessUnitForReport,
    IChecklist2ForReport,
    IChecklist2ReportChecklistSummary,
    IChecklistScheduleForReport,
    IChecklistV2FilterAvailableItems,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { ILightWeightArea, ILightWeightObject } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { FiChecklist2ReportFilterService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-filter.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { fiGetSelectedAreaIdsForFilter } from '@fi-shared/utils/fi-get-selected-area-ids-for-filter';
import { WidgetTableFilterMenuType } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter/widget-table-filter-menu-type';
import { ChecklistWidgetTableFilterDataService } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter-data.service';
import { ObjectTypes } from '@fi-shared/types';
import { WidgetTableFilterType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { Checkpoint2StatusType } from '@fi-shared/checklist/checklist-enum';

export interface IWidgetTableFilterAvailableData {
    BusinessUnits?: IBusinessUnitForReport[];
    Areas?: ILightWeightArea[];
    BusinessAreas?: ILightWeightObject[];
    ChecklistIds?: number[];
    CheckpointGroupIds?: number[];
    CheckpointIds?: number[];
    UserIds?: number[];
    SingleOptionScheduleIds?: number[];
}

export interface IFilterMenuCreateArgs {
    availableItems: IChecklistV2FilterAvailableItems;
    availableAreas: ILightWeightArea[];
    availableDataFn: () => IWidgetTableFilterAvailableData;
    widgetTableFilterTypeFn: () => WidgetTableFilterType; //we should use func for proper 'Unit performance benchmark' working
    auditMode: boolean;
    isChecklistReportFilter: boolean;
    historyDataSummary: IChecklist2ReportChecklistSummary[];
    availableChecklistIds: number[];
    isChecklistLimited: boolean;
    availableChecklistScheduleIdsByData: number[];
    // isUnitBenchmarkReport: boolean;
}

interface IFilterMenuUpdateArgs {
    menusData: ISharingMenuData<INameIdObj>[];
    filter: IChecklistV2ReportParameterFilter;
    isUnitBenchmarkFilter: boolean;
    isFoldedScheduleCheckpointMenus: (menuData: ISharingMenuData<INameIdObj>) => boolean;
    isChecklistReportFilter: boolean;
    availableItems: IChecklistV2FilterAvailableItems;
}

@Injectable()
export class ChecklistWidgetTableFilterHelperService {
    constructor(
        private appTextService: AppTextService,
        private fiChecklist2ReportFilterService: FiChecklist2ReportFilterService,
        private fiWidgetTableFilterDataService: ChecklistWidgetTableFilterDataService,
        private fiChecklist2Service: FiChecklist2Service,
    ) {}

    //#region initialize filter menu data
    getFilterMenusData(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdObj>[] {
        const menusData = [];
        menusData.push(this.getChecklistsMenu(args));
        menusData.push(this.getChecklistSchedulesMenu(args));
        menusData.push(this.getCheckpointGroupsMenu(args));
        menusData.push(this.getCheckpointsMenu(args));
        menusData.push(this.getAnswersMenu(args));
        menusData.push(this.getUsersMenu(args));
        menusData.push(this.getBusinessUnitMenu(args));
        menusData.push(this.getAreaMenu(args));
        menusData.push(this.getBusinessAreaMenu(args));
        return menusData;
    }

    isSelectedItemRemovable(
        widgetTableFilterType: WidgetTableFilterType,
        filterType: FiFilterType,
        isAllItemsRemovable: boolean,
        currentFilter: IChecklistV2ReportParameterFilter,
        notRemovableFilterTypes: FiFilterType[],
        availableBusinessUnitCount: number,
        auditMode: boolean,
    ): boolean {
        if (isAllItemsRemovable) return true;
        if (notRemovableFilterTypes.length) {
            if (notRemovableFilterTypes.includes(filterType)) {
                if (filterType === FiFilterType.BusinessUnit) {
                    return this.isBusinessUnitMenuAvailable(availableBusinessUnitCount, auditMode);
                }
            }
            return true;
        }
        if ([FiFilterType.Checklist, FiFilterType.Schedule].includes(filterType)) {
            // prevent removing selected checklist / schedule if only one item selected
            if (filterType === FiFilterType.Checklist && currentFilter?.ChecklistIds?.length > 1) {
                return true;
            }
            if (filterType === FiFilterType.Schedule && currentFilter?.ScheduleIds?.length > 1) {
                return true;
            }
            return false;
        }
        if (
            filterType === FiFilterType.Checkpoint &&
            widgetTableFilterType === WidgetTableFilterType.RecentActivityCheckpointDetails
        ) {
            // prevent removing selected checkpoint in FiDeviationChecklistDetailsComponent
            return false;
        }

        return true;
    }

    isHistoryReport(widgetTableFilterType: WidgetTableFilterType) {
        return this.fiWidgetTableFilterDataService.isHistoryReport(widgetTableFilterType);
    }

    private getChecklistsMenu(args: IFilterMenuCreateArgs): ISharingMenuData<IChecklist2ForReport> {
        return {
            label: this.appTextService.get(
                this.fiWidgetTableFilterDataService.isChecklistsAndSchedulesMenu(args)
                    ? 'checklist.checklist_and_schedules'
                    : 'checklist.module_name',
            ),
            menuItems: this.fiWidgetTableFilterDataService.getChecklistMenuItems(args),
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.Checklist,
            childrenFilterType: FiFilterType.Schedule,
            selectedItemIds: [],
            isSubmenuLayout: this.fiWidgetTableFilterDataService.isChecklistsAndSchedulesMenu(args),
            isAvailable: () =>
                !args.isChecklistReportFilter ||
                this.fiWidgetTableFilterDataService.isHistoryAttachmentsList(args.widgetTableFilterTypeFn()),

            /* && this.isMoreThanOneOptionAvailable(this.availableItems.AvailableChecklists)*/ // isDisabled: args.isHistoryReport && !!args.currentFilter.CheckpointIds?.length,
            isOptionHidden: (item: IChecklist2ForReport) => {
                // https://chainformation.atlassian.net/browse/FI-7007
                // isOptionHidden is invoked both for checklists and connected schedules
                // todo split getChecklistsMenu to 2 methods, depeneding from isChecklistsAndSchedulesMenu:
                // - checklists menu
                // - checklists with connected schedules menu
                if (item['IsSchedule'] === true) {
                    return args.availableChecklistScheduleIdsByData?.length
                        ? !args.availableChecklistScheduleIdsByData.includes(item.Id)
                        : false;
                }
                return args.isChecklistLimited && args.availableChecklistIds?.length
                    ? !args.availableChecklistIds.includes(item.Id)
                    : false;
            },
        };
    }

    private getChecklistSchedulesMenu(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdObj> {
        // simple schedules dropdown is used in 'fi-open-checklist-details' and 'fi-checklist-attachments-list'
        return {
            label: this.appTextService.get('checklist.checklist_schedules'),
            menuItems: args.availableItems.Schedules,
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.Schedule,
            selectedItemIds: [],
            isOptionHidden: (item: IChecklistScheduleForReport) => {
                return !args.availableDataFn().SingleOptionScheduleIds.includes(item.Id);
            },
            isAvailable: () =>
                args.isChecklistReportFilter &&
                !this.fiWidgetTableFilterDataService.isHistoryAttachmentsList(
                    args.widgetTableFilterTypeFn(),
                ) /*&& this.isMoreThanOneOptionAvailable(args.availableSchedules) */,
        };
    }

    private getCheckpointsMenu(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdTypeObjWithChildren> {
        const items: INameIdTypeObjWithChildren[] = this.fiWidgetTableFilterDataService.getCheckpointMenuItemsForMenu(
            undefined,
            args.availableItems.Checklists,
            args.availableItems.CheckpointGroups,
            args.availableItems.Checkpoints,
        );
        return {
            label: this.appTextService.get('checklist.checkpoints'),
            menuItems: fiItemsFlatten(items),
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item: INameIdTypeObjWithChildren) =>
                this.isCheckpointMenuItemOptionHidden(item, args.availableDataFn()),
            filterType: FiFilterType.Checkpoint,
            selectedItemIds: undefined,
            selectedExpandableItems: [],
            isAvailable: () => {
                const widgetTableFilterType = args.widgetTableFilterTypeFn();
                // hide for units benchmark by checklists and checkpoint groups
                return (
                    (this.isHistoryReport(args.widgetTableFilterTypeFn()) &&
                        widgetTableFilterType !== WidgetTableFilterType.UnitBenchmarkByChecklists &&
                        widgetTableFilterType !== WidgetTableFilterType.UnitBenchmarkByCheckpointGroups) ||
                    this.fiWidgetTableFilterDataService.isAttachmentsList(args.widgetTableFilterTypeFn())
                );
            },
            // expandable item search menu props
            selectionObjectTypes: [ObjectTypes.Checkpoint2],
            expandableObjectTypes: [ObjectTypes.CheckpointGroup],
            menuComponentAdditionalClass: 'checkpoints-menu',
        };
    }

    private getCheckpointGroupsMenu(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdTypeObjWithChildren> {
        const items: INameIdTypeObjWithChildren[] = this.fiWidgetTableFilterDataService.getCheckpointMenuItemsForMenu(
            undefined,
            args.availableItems.Checklists,
            args.availableItems.CheckpointGroups,
            args.availableItems.Checkpoints,
        );
        return {
            label: this.appTextService.get('checklist.add_section'),
            menuItems: fiItemsFlatten(items),
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item: INameIdTypeObjWithChildren) =>
                this.isCheckpointMenuItemOptionHidden(item, args.availableDataFn()),
            filterType: FiFilterType.CheckpointGroup,
            selectedItemIds: undefined,
            selectedExpandableItems: [],
            isAvailable: () => {
                const widgetTableFilterType = args.widgetTableFilterTypeFn();
                // hide for units benchmark by checklists and checkpoints
                return (
                    (this.isHistoryReport(args.widgetTableFilterTypeFn()) &&
                        widgetTableFilterType !== WidgetTableFilterType.UnitBenchmarkByChecklists &&
                        widgetTableFilterType !== WidgetTableFilterType.UnitBenchmarkByCheckpoints) ||
                    this.fiWidgetTableFilterDataService.isAttachmentsList(args.widgetTableFilterTypeFn())
                );
            },
            // expandable item search menu props
            selectionObjectTypes: [ObjectTypes.CheckpointGroup],
            expandableObjectTypes: [ObjectTypes.CheckpointGroup],
            menuComponentAdditionalClass: 'checkpoint-groups-menu',
        };
    }

    private isCheckpointMenuItemOptionHidden(
        item: INameIdTypeObjWithChildren,
        availableData: IWidgetTableFilterAvailableData,
    ) {
        let availableIds: number[];
        if (item.ObjectType === ObjectTypes.Checklist2) {
            availableIds = availableData.ChecklistIds;
        } else if (item.ObjectType === ObjectTypes.CheckpointGroup) {
            availableIds = availableData.CheckpointGroupIds;
        } else if (item.ObjectType === ObjectTypes.Checkpoint2) {
            availableIds = availableData.CheckpointIds;
        }
        return availableIds && !availableIds.includes(item.Id);
    }

    private isBusinessUnitMenuAvailable(availableBusinessUnitCount: number, auditMode: boolean): boolean {
        return this.fiChecklist2Service.isReportWriteRights(auditMode) && availableBusinessUnitCount > 1;
    }

    private getBusinessUnitMenu(args: IFilterMenuCreateArgs): ISharingMenuData<IBusinessUnitForReport> {
        return {
            label: this.appTextService.get('common.units'),
            menuItems: args.availableItems.BusinessUnits,
            displayFieldPropertyName: 'Name',
            filterType: FiFilterType.BusinessUnit,
            isOptionHidden: (item) => !args.availableDataFn().BusinessUnits.includes(item),
            selectedItemIds: [],
            isAvailable: () =>
                this.isBusinessUnitMenuAvailable(args.availableItems.BusinessUnits?.length, args.auditMode),
            /* && this.isMoreThanOneOptionAvailable(this.availableData.BusinessUnits)*/
        };
    }

    private getAreaMenu(args: IFilterMenuCreateArgs): ISharingMenuData<ILightWeightArea> {
        return {
            label: this.appTextService.get('common.areas'),
            menuItems: args.availableAreas?.filter((area: ILightWeightArea) => !area.ParentId),
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => !args.availableDataFn().Areas.includes(item),
            filterType: FiFilterType.Area,
            childrenFilterType: FiFilterType.Area,
            selectedItemIds: [],
            selectedChildItemIds: [],
            isSubmenuLayout: true,
            isAvailable: () =>
                this.fiWidgetTableFilterDataService.isAreaAvailableByFilterType(args.widgetTableFilterTypeFn()) &&
                //&& this.fiChecklist2Service.isReportShareRights({auditMode: args.auditMode})
                args.availableItems.Areas.length > 1,
            /* && this.isMoreThanOneOptionAvailable(this.availableData.Areas)*/
        };
    }

    private getBusinessAreaMenu(args: IFilterMenuCreateArgs): ISharingMenuData<ILightWeightObject> {
        //https://chainformation.atlassian.net/browse/FI-6333
        const isBusinessAreaMenuHidden =
            args.availableItems.BusinessAreas.length <= 1 || args.availableItems.BusinessUnits.length <= 1;

        return {
            label: this.appTextService.get('common.business_areas'),
            menuItems: args.availableItems.BusinessAreas,
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => !args.availableDataFn().BusinessAreas.includes(item),
            filterType: FiFilterType.BusinessArea,
            selectedItemIds: [],
            isAvailable: () =>
                this.fiWidgetTableFilterDataService.isAreaAvailableByFilterType(args.widgetTableFilterTypeFn()) &&
                //&& (this.fiChecklist2Service.isReportShareRights({auditMode: args.auditMode}) || args.availableItems.HasMasterExtendedOption)
                !isBusinessAreaMenuHidden,
            /* && this.isMoreThanOneOptionAvailable(this.availableData.BusinessAreas)*/
        };
    }

    private getUsersMenu(args: IFilterMenuCreateArgs): ISharingMenuData<ILightWeightObject> {
        return {
            label: this.appTextService.get('common.users'),
            menuItems: args.availableItems.Users.map((user) => ({ Id: user.Id, Name: user.FullName })),
            displayFieldPropertyName: 'Name',
            isOptionHidden: (item) => !args.availableDataFn().UserIds.includes(item.Id),
            filterType: FiFilterType.User,
            selectedItemIds: [],
            isAvailable: () => this.fiWidgetTableFilterDataService.isAttachmentsList(args.widgetTableFilterTypeFn()),
        };
    }

    private getAnswersMenu(args: IFilterMenuCreateArgs): ISharingMenuData<INameIdObj> {
        return {
            label: this.appTextService.get('checklist.answers'),
            menuItems: this.getAnswerMenuItems(),
            displayFieldPropertyName: 'Name',
            //isOptionHidden: (item) => false,
            filterType: FiFilterType.CheckpointStatus,
            selectedItemIds: [],
            isAvailable: () => this.fiWidgetTableFilterDataService.isAttachmentsList(args.widgetTableFilterTypeFn()),
        };
    }
    getAnswerMenuItems(): INameIdObj[] {
        return [
            { Id: Checkpoint2StatusType.Ok, Name: this.appTextService.get('ok') },
            { Id: Checkpoint2StatusType.NotOk, Name: this.appTextService.get('notOk') },
        ];
    }

    //#endregion

    //#region update menu/get available menu options

    private hideDeactivatedUnits(widgetTableFilterType: WidgetTableFilterType): boolean {
        return [
            WidgetTableFilterType.OpenChecklistsSummary,
            WidgetTableFilterType.DeviationCheckpoints,
            WidgetTableFilterType.RecentActivity,
        ].includes(widgetTableFilterType);
    }

    getAvailableMenuItems(args: {
        filter: IChecklistV2ReportParameterFilter;
        widgetTableFilterType: WidgetTableFilterType;
        availableItems: IChecklistV2FilterAvailableItems;
        checklistSummaryBusinessUnitIds: number[];
        isAttentionRequiredFilter: boolean;
        availableAreas: ILightWeightArea[];
        availableBusinessUnitIdsByData: number[];
        availableChecklistScheduleIdsByData: number[];
        availableUserIdsByData: number[];
    }): IWidgetTableFilterAvailableData {
        const availableItems = args.availableItems;
        const filter = args.filter;
        let availableBusinessUnits = availableItems.BusinessUnits;
        let availableBusinessAreas = availableItems.BusinessAreas;
        let availableAreas = args.availableAreas;
        let _fs = this.fiChecklist2ReportFilterService;

        // note: for different cases we availableBusinessUnitIds to filter initially available business unit
        // in Attention required checklist details we need to work only with units with deviations and not all availableItems.AvailableBusinessUnits;
        // in Open checklist details - we need to get display only filtered units
        if (!!args.availableBusinessUnitIdsByData?.length) {
            availableBusinessUnits = availableBusinessUnits.filter((bu) =>
                args.availableBusinessUnitIdsByData.includes(bu.Id),
            );
            availableBusinessAreas = _fs.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableBusinessUnits,
            );
        }

        // https://chainformation.atlassian.net/browse/FI-6361
        // hide deactivated units in open checklists summary, deviations, recent activity
        if (this.hideDeactivatedUnits(args.widgetTableFilterType)) {
            availableBusinessUnits = availableBusinessUnits.filter((bu) => bu.IsActivated);
        }

        let selectedSchedules: IChecklistScheduleForReport[];
        if (filter.ScheduleIds?.length) {
            // if schedule ids selected we should filter by schedule access rights
            selectedSchedules = availableItems.Schedules.filter((schedule) => filter.ScheduleIds.includes(schedule.Id));

            // filter business areas by business units
            availableBusinessAreas = _fs.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableItems.BusinessUnits,
            );
            // filter business areas by schedules
            availableBusinessAreas = _fs.filterBusinessAreasByMainItems(availableBusinessAreas, selectedSchedules);

            //availableBusinessUnits = availableBusinessUnits.filter(bu  => args.checklistSummaryBusinessUnitIds.includes(bu.Id)); // todo looks like redundant?
            // filter business unis by schedules
            availableBusinessUnits = _fs.filterBusinessUnitsByMainItems(availableBusinessUnits, selectedSchedules);

            // filter business units by business areas
            availableBusinessUnits = _fs.filterBusinessUnitsByBusinessAreaIds(
                availableBusinessUnits,
                availableBusinessAreas.map((ba) => ba.Id),
            );

            // filter areas by business units and schedules
            availableAreas = _fs.filterAreasByBusinessUnits(availableAreas, availableBusinessUnits, selectedSchedules);
        }

        let availableSingleOptionScheduleIds: number[] = [];
        if (filter.ChecklistIds?.length) {
            availableItems.Checklists.filter((checklist) => filter.ChecklistIds.includes(checklist.Id)).forEach(
                (checklist) => availableSingleOptionScheduleIds.push(...checklist.ChecklistScheduleIds),
            );
        }
        if (args.availableChecklistScheduleIdsByData) {
            availableSingleOptionScheduleIds = availableSingleOptionScheduleIds.filter((scheduleId) =>
                args.availableChecklistScheduleIdsByData.includes(scheduleId),
            );
        }

        // https://chainformation.atlassian.net/browse/FI-5689
        if (args.isAttentionRequiredFilter) {
            availableBusinessAreas = _fs.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableBusinessUnits,
            );
        }

        if (filter.AreaIds?.length) {
            const selectedAreaIds = fiItemsFlatten(
                availableItems.Areas.filter((area) => filter.AreaIds.includes(area.Id)),
            ).map((area) => area.Id);
            availableBusinessUnits = _fs.filterBusinessUnitsByAreaIds(availableBusinessUnits, selectedAreaIds);
            availableBusinessAreas = _fs.filterBusinessAreasByBusinessUnits(
                availableBusinessAreas,
                availableBusinessUnits,
            );
        }

        if (filter.BusinessAreaIds?.length) {
            availableBusinessUnits = _fs.filterBusinessUnitsByBusinessAreaIds(
                availableBusinessUnits,
                filter.BusinessAreaIds,
            );

            const availableUnitsByBusinessAreas = _fs.filterBusinessUnitsByBusinessAreaIds(
                availableItems.BusinessUnits,
                filter.BusinessAreaIds,
            );
            availableAreas = _fs.filterAreasByBusinessUnits(
                availableAreas,
                availableUnitsByBusinessAreas,
                selectedSchedules,
            );
        }

        // let availableChecklistSchedules: IChecklist2ForReportWithChildren<IChecklistScheduleForReport>[] = []
        /*if (args.isHistoryReport) {
            // availableChecklistSchedules = this.getChecklistsSchedulesFoldedOptionsFilterData(filter.ChecklistIds, args.availableItems.AvailableChecklists, args.availableItems.AvailableSchedules, args.historyDataSummary)
        }*/

        const checklistIds = filter.ChecklistIds;
        return {
            BusinessUnits: availableBusinessUnits,
            Areas: availableAreas,
            BusinessAreas: availableBusinessAreas,
            ChecklistIds: checklistIds, // for filtering checkpoint groups and checkpoints
            CheckpointGroupIds: _fs
                .filterCheckpointsOrGroupsByChecklistIds(args.availableItems.CheckpointGroups, checklistIds)
                .map((item) => item.Id),
            CheckpointIds: _fs
                .filterCheckpointsOrGroupsByChecklistIds(args.availableItems.Checkpoints, checklistIds)
                .map((item) => item.Id),
            UserIds: args.availableUserIdsByData
                ? args.availableUserIdsByData
                : args.availableItems.Users.map((item) => item.Id),
            SingleOptionScheduleIds: availableSingleOptionScheduleIds,
        };
    }

    private filterNameIdTypeObjWithChildrenItems(
        items: any[],
        selectedItemIds: number[],
        objectType: ObjectTypes,
    ): INameIdTypeObjWithChildren[] {
        return items.filter((item) => selectedItemIds.includes(item.Id) && item.ObjectType === objectType);
    }

    setMenuSelectionFromFilter(args: IFilterMenuUpdateArgs) {
        const filter = args.filter;
        args.menusData.forEach((menuItem) => {
            if (menuItem.filterType === FiFilterType.Checklist) {
                menuItem.selectedItemIds = filter.ChecklistIds ?? [];
                menuItem.selectedChildItemIds =
                    menuItem.childrenFilterType === FiFilterType.Schedule
                        ? (filter.ScheduleIds ?? [])
                        : (filter.CheckpointIds ?? []);
            } else if (menuItem.filterType == FiFilterType.Schedule) {
                menuItem.selectedItemIds = filter.ScheduleIds ?? [];
            } else if (menuItem.filterType == FiFilterType.CheckpointGroup) {
                menuItem.selectedExpandableItems = filter.CheckpointGroupIds
                    ? this.filterNameIdTypeObjWithChildrenItems(
                          menuItem.menuItems,
                          filter.CheckpointGroupIds,
                          ObjectTypes.CheckpointGroup,
                      )
                    : [];
            } else if (menuItem.filterType == FiFilterType.Checkpoint) {
                menuItem.selectedExpandableItems = filter.CheckpointIds
                    ? this.filterNameIdTypeObjWithChildrenItems(
                          menuItem.menuItems,
                          filter.CheckpointIds,
                          ObjectTypes.Checkpoint2,
                      )
                    : [];
            } else if (menuItem.filterType == FiFilterType.BusinessUnit) {
                menuItem.selectedItemIds = filter.BusinessUnitIds ?? [];
            } else if (menuItem.filterType == FiFilterType.Area) {
                menuItem.selectedItemIds = fiGetSelectedAreaIdsForFilter(args.availableItems.Areas, filter.AreaIds);
                menuItem.selectedChildItemIds = fiGetSelectedAreaIdsForFilter(
                    args.availableItems.Areas,
                    filter.AreaIds,
                    true,
                );
            } else if (menuItem.filterType == FiFilterType.BusinessArea) {
                menuItem.selectedItemIds = filter.BusinessAreaIds ?? [];
            } else if (menuItem.filterType == FiFilterType.User) {
                menuItem.selectedItemIds = filter.UserIds ?? [];
            } else if (menuItem.filterType == FiFilterType.CheckpointStatus) {
                menuItem.selectedItemIds = filter.CheckpointStatusTypes ?? [];
            }
        });
    }

    setMenuDisabledState(args: IFilterMenuUpdateArgs) {
        const filter = args.filter;
        args.menusData.forEach((menuItem) => {
            if (menuItem.filterType === FiFilterType.CheckpointGroup) {
                if (!args.isUnitBenchmarkFilter) {
                    menuItem.isDisabled = args.filter.ChecklistIds?.length > 1 || !!filter.CheckpointIds?.length;
                } else {
                    menuItem.isDisabled = !!filter.CheckpointIds?.length;
                }
            }

            if (menuItem.filterType === FiFilterType.Checkpoint) {
                if (!args.isUnitBenchmarkFilter) {
                    menuItem.isDisabled = args.filter.ChecklistIds?.length > 1 || !!filter.CheckpointGroupIds?.length;
                } else {
                    menuItem.isDisabled = !!filter.CheckpointGroupIds?.length;
                }
            }

            if ([FiFilterType.Area, FiFilterType.BusinessArea].includes(menuItem.filterType)) {
                menuItem.isDisabled = !!filter.BusinessUnitIds?.length;
            }
            if (args.isUnitBenchmarkFilter) {
                menuItem.isDisabled =
                    !filter.ScheduleIds?.length &&
                    (menuItem.filterType === FiFilterType.BusinessUnit ||
                        menuItem.filterType === FiFilterType.Checkpoint ||
                        menuItem.filterType === FiFilterType.CheckpointGroup);
            }
            // if (menuItem.filterType === FiFilterType.Unit && args.isUnitBenchmarkFilter) {
            //     menuItem.isDisabled = !filter.ScheduleIds?.length;

            // return !!this.currentFilter.AreaIds?.length || !!this.currentFilter.BusinessAreaIds?.length;
            // }
            // if (args.isUnitBenchmarkFilter && menuItem.childrenFilterType === FiFilterType.ChecklistCheckpoint) {
            //     menuItem.isDisabled = !args.filter.ScheduleIds?.length;
            // }
        });
    }

    //#endregion

    getMenuDataByFilterType(
        menusData: ISharingMenuData<INameIdObj>[],
        filterType: FiFilterType,
        isChildMenuItemType?: boolean,
    ): ISharingMenuData<INameIdObj> {
        return menusData.find((menuData: ISharingMenuData<INameIdObj>) => {
            return isChildMenuItemType
                ? menuData.childrenFilterType === filterType
                : menuData.filterType === filterType;
        });
    }

    getSelectedScheduleFilterItems(
        selectedFilterItems: ISelectedFilterItem[],
        checklistId: number,
        availableChecklists: IChecklist2ForReport[],
    ): ISelectedFilterItem[] {
        const relatedScheduleIds: number[] = this.fiWidgetTableFilterDataService.getRelatedSchedulesIds(
            checklistId,
            availableChecklists,
        );

        return selectedFilterItems.filter((selectedFilterItem: ISelectedFilterItem) => {
            const itemId: number = selectedFilterItem.value as number;
            return selectedFilterItem.filterType === FiFilterType.Schedule && relatedScheduleIds.includes(itemId);
        });
    }

    getSelectedFilterItemsByType(
        selectedFilterItems: ISelectedFilterItem[],
        filterType: FiFilterType,
    ): ISelectedFilterItem[] {
        return selectedFilterItems.filter((selectedFilterItem: ISelectedFilterItem) => {
            return selectedFilterItem.filterType === filterType;
        });
    }

    getSelectedFilterChecklistItemById(
        selectedFilterItems: ISelectedFilterItem[],
        checklistId: number,
    ): ISelectedFilterItem {
        return selectedFilterItems.find((selectedFilterItem: ISelectedFilterItem) => {
            return selectedFilterItem.filterType === FiFilterType.Checklist && selectedFilterItem.value === checklistId;
        });
    }

    getRelatedChecklistIdByScheduleId(scheduleId: number, availableChecklists: IChecklist2ForReport[]): number {
        return availableChecklists.find((checklist) => {
            return checklist.ChecklistScheduleIds.includes(scheduleId);
        }).Id;
    }

    //# region widget table filter menu type
    private readonly menuFilterTypeAlignedWithChildren: FiFilterType[] = [FiFilterType.Area];
    private readonly foldedOptionsChildMenuTypes: FiFilterType[] = [FiFilterType.Schedule];
    private readonly menuFilterTypeExpandable: FiFilterType[] = [FiFilterType.Checkpoint, FiFilterType.CheckpointGroup];

    isFoldedFilterMenu(
        filterType: FiFilterType,
        widgetTableFilterType: WidgetTableFilterType,
        isHistoryReport: boolean,
        isRecentActivityWidget: boolean,
    ): boolean {
        return (
            (isHistoryReport ||
                isRecentActivityWidget ||
                this.fiWidgetTableFilterDataService.isAttachmentsList(widgetTableFilterType)) &&
            this.foldedOptionsChildMenuTypes.includes(filterType)
        );
    }

    getFilterMenuType(
        menuData: ISharingMenuData<INameIdObj>,
        widgetTableFilterType: WidgetTableFilterType,
        isHistoryReport: boolean,
        isRecentActivityWidget: boolean,
    ): WidgetTableFilterMenuType {
        if (this.menuFilterTypeAlignedWithChildren.includes(menuData.filterType))
            // areas
            return WidgetTableFilterMenuType.AlignedWithChildren;

        if (this.menuFilterTypeExpandable.includes(menuData.filterType))
            // checkpoints and checkpoint groups
            return WidgetTableFilterMenuType.Expandable;

        if (menuData.isSubmenuLayout) return WidgetTableFilterMenuType.WithSubMenu;

        if (
            this.isFoldedFilterMenu(
                menuData.childrenFilterType,
                widgetTableFilterType,
                isHistoryReport,
                isRecentActivityWidget,
            )
        ) {
            return WidgetTableFilterMenuType.FoldedSchedules;
        }

        if (!(isHistoryReport || isRecentActivityWidget) && menuData.filterType === FiFilterType.Schedule) {
            return WidgetTableFilterMenuType.SingleSelectOption;
        }

        return WidgetTableFilterMenuType.MultiSelectOption;
    }

    //#endregion

    getSelectedAreaIdsForFilter(
        availableItems: ILightWeightArea[],
        selectedItemIds: number[],
    ): {
        selectedItems: number[];
        selectedChildItems: number[];
    } {
        return {
            selectedItems: fiGetSelectedAreaIdsForFilter(availableItems, selectedItemIds),
            selectedChildItems: fiGetSelectedAreaIdsForFilter(availableItems, selectedItemIds, true),
        };
    }
}
